import { useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { IonContent, IonPage } from '@ionic/react';
import AuthenticatedExperience from '../components/figma/AuthenticatedExperience';
import MobilePreview from '../components/MobilePreview';
import ScaledFigmaCanvas from '../components/figma/ScaledFigmaCanvas';
import { FIGMA_BASE_HEIGHT, FIGMA_BASE_WIDTH } from '../components/figma/figma.constants';
import { useAuth } from '../context/AuthContext';
import { fetchModules, updateModule } from '../lib/modules';
import type { CourseModule } from '../types/course';
import './Home.css';

const AppDashboard: React.FC = () => {
  const history = useHistory();
  const { session, isAuthenticated, refreshSession, updateSessionScore } = useAuth();
  const [modules, setModules] = useState<CourseModule[]>([]);

  useEffect(() => {
    if (!session?.sessionId) {
      return;
    }
    refreshSession().catch((error) => {
      console.error('Unable to refresh session', error);
    });
  }, [refreshSession, session?.sessionId]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    let isMounted = true;
    fetchModules()
      .then((data) => {
        if (isMounted) {
          setModules(data);
        }
      })
      .catch((error) => {
        console.error('Unable to fetch modules', error);
      });
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  const handleStartModule = (module: CourseModule) => {
    history.push(`/app/modules/${module.id}/lesson`, { module });
  };

  const handleToggleModuleLock = async (module: CourseModule) => {
    try {
      const updated = await updateModule(module.id, { isLocked: !module.isLocked });
      setModules((previous) => previous.map((item) => (item.id === updated.id ? updated : item)));
    } catch (error) {
      console.error('Unable to update module', error);
    }
  };

  const handleGoHome = async () => {
    if (session && session.score !== 0) {
      try {
        await updateSessionScore(0);
      } catch (error) {
        console.error('Unable to reset score', error);
      }
    }
    history.push('/home');
  };

  const modulesToDisplay = useMemo(() => modules, [modules]);

  if (!isAuthenticated || !session) {
    return <Redirect to="/home" />;
  }

  return (
    <IonPage>
      <IonContent fullscreen className="home-content">
        <MobilePreview ariaLabel="Espace quiz">
          <ScaledFigmaCanvas baseWidth={FIGMA_BASE_WIDTH} baseHeight={FIGMA_BASE_HEIGHT} ariaLabel="Tableau de bord">
            <AuthenticatedExperience
              fullName={session.user.fullName}
              score={session.score}
              modules={modulesToDisplay}
              onStartModule={handleStartModule}
              onToggleModuleLock={handleToggleModuleLock}
              onGoHome={handleGoHome}
            />
          </ScaledFigmaCanvas>
        </MobilePreview>
      </IonContent>
    </IonPage>
  );
};

export default AppDashboard;
